import {StoredImageDto} from "../dtos/stored-image.dto";
import {logInfo} from "./logger.service";
import {getAccessTokenOrThrow} from "./auth.service";

export const uploadImage = async (file: File): Promise<StoredImageDto> => {
  if(!file) throw new Error('No file provided')

  const formData = new FormData();
  formData.append('file', file);

  const token = await getAccessTokenOrThrow();
  const image = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/images`,
    {
      method: 'POST',
      body: formData,
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).then(async response => {
      if(!response.ok) {
        throw new Error(`Failed to upload image: ${response.statusText}`);
      }
      return await response.json() as StoredImageDto
  });

  logInfo(`Image uploaded: ${image.id}`);
  return image;
};

/** One image with its original and its editor marks (GET images/{id}). */
export const getImage = async (id: number): Promise<StoredImageDto> => {
  const token = await getAccessTokenOrThrow();
  const response = await fetch(`${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/images/${id}`, {
    headers: {Authorization: `Bearer ${token}`},
  });
  if (!response.ok) throw new Error(`Failed to load image: ${response.statusText}`);
  return await response.json() as StoredImageDto;
};

/**
 * Replaces the photo's shown copy in place (the server keeps the original).
 * `annotations`: the marks document the copy was drawn from, so a later edit
 * can move or remove them; without it the server clears any earlier marks.
 */
export const updateImage = async (id: number, file: File, annotations?: string): Promise<StoredImageDto> => {
  if(!file) throw new Error('No file provided')

  const formData = new FormData();
  formData.append('file', file);
  if (annotations) formData.append('annotations', annotations);

  const token = await getAccessTokenOrThrow();
  const image = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/images/${id}`,
    {
      method: 'PATCH',
      body: formData,
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).then(async response => {
      if(!response.ok) {
          throw new Error(`Failed to update image: ${response.statusText}`);
      }
      return await response.json() as StoredImageDto
  });

  logInfo(`Image updated: ${image.id}`);
  return image;
};

export const resetImage = async (id: number): Promise<StoredImageDto> => {
  const token = await getAccessTokenOrThrow();
  const image = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/images/${id}/reset`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).then(async response => {
      if(!response.ok) {
          throw new Error(`Failed to reset image: ${response.statusText}`);
      }
      return await response.json() as StoredImageDto
  });

  logInfo(`Image reset: ${image.id}`);
  return image;
};

/** What the photo editor hands back when the person presses Done. */
export type PhotoEdit =
  /** Upload `file` as the photo's shown copy, with its marks document. */
  | {kind: 'render'; file: File; annotations?: string}
  /** Everything was taken off: back to the original, no upload needed. */
  | {kind: 'reset'};

/** Saves an editor result: the rendered copy with its marks, or a reset to the original. */
export const saveImageEdit = (id: number, edit: PhotoEdit): Promise<StoredImageDto> =>
  edit.kind === 'reset' ? resetImage(id) : updateImage(id, edit.file, edit.annotations);
